const FALLBACK_EXCLUDED = [
  'discharge summary (pt)', 'discharge summary (ot)', 'discharge summary (st)',
  'nb - supervisory visit (pt)', 'nb - supervisory visit (ot)',
  'nb hha sup visit (pt)',
  '5 day discharge notice ot', '5 day discharge notice pt', '5 day discharge notice st',
  'oasis-e1 discharge (non-billable) - ot', 'oasis-e1 discharge (non-billable) - pt',
  'oasis-e1 discharge (non-billable) - st',
  'case conference - nb (pt)', 'case conference - nb (ot)', 'case conference - nb (st)'
];

/**
 * Calculate the weight of a visit based on tracking config.
 * This is the SINGLE SOURCE OF TRUTH used by Dashboard, Therapist Reports, and Agency Reports.
 */
export function getVisitWeight(taskName, status, trackingConfig) {
  const tn = (taskName || '').toLowerCase();
  const st = (status || '').toLowerCase();

  if (st.includes('missed') || st.includes('mv') || st.includes('m/v')) return 0;

  if (trackingConfig?.configured && trackingConfig.weight_map) {
    const wm = trackingConfig.weight_map;
    const excluded = trackingConfig.excluded_tasks || [];
    if (excluded.includes(tn)) return 0;
    if (tn in wm) return wm[tn];

    let bestWeight = null;
    let bestLen = 0;
    for (const [key, weight] of Object.entries(wm)) {
      if (tn.includes(key) && key.length > bestLen) {
        bestWeight = weight;
        bestLen = key.length;
      }
    }
    if (bestWeight !== null) return bestWeight;
    if (FALLBACK_EXCLUDED.includes(tn)) return 0;
    // OASIS fallback: config may not cover all OASIS variants (e.g. E1 vs E2)
    if (tn.includes('oasis')) {
      if (tn.includes('nb') || tn.includes('non-billable')) return 0;
      return 2;
    }
    return 1;
  }

  if (FALLBACK_EXCLUDED.includes(tn)) return 0;
  if (tn.includes('oasis')) {
    if (tn.includes('nb') || tn.includes('non-billable')) return 0;
    return 2;
  }
  return 1;
}

/**
 * Parse a visit date string into a JS Date.
 * Handles MM/DD/YYYY and YYYY-MM-DD formats.
 */
export function parseVisitDate(dateStr) {
  if (!dateStr) return null;
  if (dateStr.includes('/')) {
    const [m, d, y] = dateStr.split('/');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d), 12, 0, 0);
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Get the 6 most recent completed weeks (Sunday-Saturday).
 */
export function getRecentWeeks(count = 6) {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysBack = dayOfWeek === 6 ? 7 : dayOfWeek + 1;
  const latestSaturday = new Date(now);
  latestSaturday.setDate(now.getDate() - daysBack);
  latestSaturday.setHours(23, 59, 59, 999);

  const weeks = [];
  for (let i = 0; i < count; i++) {
    const saturday = new Date(latestSaturday);
    saturday.setDate(latestSaturday.getDate() - (i * 7));
    saturday.setHours(23, 59, 59, 999);
    const sunday = new Date(saturday);
    sunday.setDate(saturday.getDate() - 6);
    sunday.setHours(0, 0, 0, 0);
    weeks.unshift({ sunday, saturday });
  }
  return weeks;
}

/**
 * Compute the Final (last completed week) and Projected totals
 * from visits + projections using the shared weight function.
 */
export function computeDashboardTotals(visits, projections, trackingConfig) {
  const weeks = getRecentWeeks(6);
  const finalWeek = weeks[weeks.length - 1];

  // Final: sum weighted visits in last completed week
  let finalCount = 0;
  (visits || []).forEach(v => {
    const weight = getVisitWeight(v.task_name, v.status, trackingConfig);
    if (weight === 0) return;
    const visitDate = parseVisitDate(v.date);
    if (visitDate && visitDate >= finalWeek.sunday && visitDate <= finalWeek.saturday) {
      finalCount += weight;
    }
  });

  // Projected: sum weighted projection visits for the most recent available projection week
  const now = new Date();
  const dayOfWeek = now.getDay();
  const nextSaturday = new Date(now);
  if (dayOfWeek !== 6) {
    nextSaturday.setDate(now.getDate() + (6 - dayOfWeek));
  }

  // Find the latest projection week_ending in the data (Proj 1 only)
  let latestProjWeek = null;
  (projections || []).forEach(proj => {
    if (proj.projection_type === 'Proj 1' && proj.week_ending) {
      if (!latestProjWeek || proj.week_ending > latestProjWeek) {
        latestProjWeek = proj.week_ending;
      }
    }
  });

  // Use the latest available projection week (fallback to next Saturday)
  const projWeekEnding = latestProjWeek || `${nextSaturday.getFullYear()}-${String(nextSaturday.getMonth() + 1).padStart(2, '0')}-${String(nextSaturday.getDate()).padStart(2, '0')}`;
  // For the label, parse the actual projection week date
  const projDate = latestProjWeek ? new Date(latestProjWeek + 'T00:00:00') : nextSaturday;

  let projectedCount = 0;
  (projections || []).forEach(proj => {
    if (proj.week_ending === projWeekEnding && proj.projection_type === 'Proj 1') {
      (proj.visit_details || []).forEach(detail => {
        projectedCount += getVisitWeight(
          detail.visit_name || detail.task_name,
          detail.status,
          trackingConfig
        );
      });
    }
  });

  const finalLabel = `${String(finalWeek.saturday.getMonth() + 1).padStart(2, '0')}/${String(finalWeek.saturday.getDate()).padStart(2, '0')}`;
  const projLabel = `${String(projDate.getMonth() + 1).padStart(2, '0')}/${String(projDate.getDate()).padStart(2, '0')}`;

  return { finalCount, projectedCount, finalLabel, projLabel };
}

/**
 * Compute per-week Final (actuals) and Projected (Proj 1) totals for the last
 * `count` completed weeks. Optionally filter by agency (substring match).
 * Returns most-recent-first for display.
 */
export function computeWeeklyHistory(visits, projections, trackingConfig, count = 12, agencyFilter = '') {
  const weeks = getRecentWeeks(count);
  const af = (agencyFilter || '').trim().toLowerCase();
  const fmtWeek = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const matchAgency = (name) => !af || (name || '').toLowerCase().includes(af);

  const rows = weeks.map(({ sunday, saturday }) => {
    let finalCount = 0;
    (visits || []).forEach((v) => {
      if (!matchAgency(v.agency)) return;
      const weight = getVisitWeight(v.task_name, v.status, trackingConfig);
      if (weight === 0) return;
      const d = parseVisitDate(v.date);
      if (d && d >= sunday && d <= saturday) finalCount += weight;
    });

    const weekStr = fmtWeek(saturday);
    let projectedCount = 0;
    (projections || []).forEach((proj) => {
      if (proj.projection_type !== 'Proj 1' || proj.week_ending !== weekStr) return;
      if (af && proj.agency && !String(proj.agency).toLowerCase().includes(af)) return;
      (proj.visit_details || []).forEach((detail) => {
        if (af && detail.agency && !String(detail.agency).toLowerCase().includes(af)) return;
        projectedCount += getVisitWeight(detail.visit_name || detail.task_name, detail.status, trackingConfig);
      });
    });

    const label = `${String(saturday.getMonth() + 1).padStart(2, '0')}/${String(saturday.getDate()).padStart(2, '0')}/${saturday.getFullYear()}`;
    return { weekEnding: weekStr, weekEndingLabel: label, finalCount, projectedCount };
  });

  return rows.reverse();
}

/**
 * Pivot the last `count` completed weeks into an Agency × Weeks matrix.
 * `metric` is 'final' (weighted actual visits) or 'projected' (weighted Proj 1).
 * Columns run oldest → newest (left → right). Optional agency substring filter.
 * Returns { weeks: [{weekEnding,label}], rows: [{agency, values:[]}], totals:[] }.
 */
export function computeAgencyWeeklyMatrix(visits, projections, trackingConfig, metric = 'final', count = 12, agencyFilter = '') {
  const weekObjs = getRecentWeeks(count); // oldest → newest
  const af = (agencyFilter || '').trim().toLowerCase();
  const fmtWeek = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const matchAgency = (name) => !af || (name || '').toLowerCase().includes(af);

  const weeks = weekObjs.map(({ saturday }) => ({
    weekEnding: fmtWeek(saturday),
    label: `${String(saturday.getMonth() + 1).padStart(2, '0')}/${String(saturday.getDate()).padStart(2, '0')}/${saturday.getFullYear()}`,
  }));
  const weekIndex = {};
  weeks.forEach((w, i) => { weekIndex[w.weekEnding] = i; });

  // agencyMap: normalized name -> { display, values: number[count] }
  const agencyMap = new Map();
  const bump = (rawName, wkIdx, amount) => {
    if (wkIdx == null || wkIdx < 0) return;
    const display = (rawName && rawName.trim()) ? rawName.trim() : 'Unassigned';
    const key = display.toLowerCase();
    if (!agencyMap.has(key)) agencyMap.set(key, { agency: display, values: new Array(count).fill(0) });
    agencyMap.get(key).values[wkIdx] += amount;
  };

  if (metric === 'projected') {
    (projections || []).forEach((proj) => {
      if (proj.projection_type !== 'Proj 1') return;
      const wkIdx = weekIndex[proj.week_ending];
      if (wkIdx == null) return;
      (proj.visit_details || []).forEach((detail) => {
        const w = getVisitWeight(detail.visit_name || detail.task_name, detail.status, trackingConfig);
        if (w === 0) return;
        const agency = detail.agency || proj.agency || 'Unassigned';
        if (!matchAgency(agency)) return;
        bump(agency, wkIdx, w);
      });
    });
  } else {
    (visits || []).forEach((v) => {
      if (!matchAgency(v.agency)) return;
      const w = getVisitWeight(v.task_name, v.status, trackingConfig);
      if (w === 0) return;
      const d = parseVisitDate(v.date);
      if (!d) return;
      for (let i = 0; i < weekObjs.length; i++) {
        if (d >= weekObjs[i].sunday && d <= weekObjs[i].saturday) { bump(v.agency, i, w); break; }
      }
    });
  }

  const rows = [...agencyMap.values()].sort((a, b) => a.agency.localeCompare(b.agency));
  const totals = weeks.map((_, i) => rows.reduce((sum, r) => sum + r.values[i], 0));
  return { weeks, rows, totals };
}



export function computeWeeklyMetrics(visits, projections, trackingConfig, agencyProfiles) {
  const weeks = getRecentWeeks(6);
  const finalWeek = weeks[weeks.length - 1]; // Last completed week

  // Build agency state map
  const agencyStateMap = {};
  (agencyProfiles || []).forEach(a => {
    agencyStateMap[(a.agency_name || '').toLowerCase()] = a.state || 'Other';
  });

  // --- Final + Agency breakdown ---
  let finalCount = 0;
  let assistcareVisits = 0;
  let pathfinderVisits = 0;
  let totalMV = 0;
  let mvLA = 0;
  let mvTX = 0;

  (visits || []).forEach(v => {
    const visitDate = parseVisitDate(v.date);
    if (!visitDate || visitDate < finalWeek.sunday || visitDate > finalWeek.saturday) return;

    const status = (v.status || '').toLowerCase();
    const isMissed = status.includes('missed') || status.includes('mv') || status.includes('m/v');

    if (isMissed) {
      // Only count as MV if the task would have weight > 0 when not missed
      // (e.g., "Discharge Summary (PT)" has 0 weight and is NOT an MV)
      const naturalWeight = getVisitWeight(v.task_name, '', trackingConfig);
      if (naturalWeight > 0) {
        totalMV += 1;
        const agency = (v.agency || '').toLowerCase();
        const state = agencyStateMap[agency] || 'Other';
        if (state === 'LA') mvLA += 1;
        else if (state === 'TX') mvTX += 1;
      }
      return;
    }

    const weight = getVisitWeight(v.task_name, v.status, trackingConfig);
    if (weight > 0) {
      finalCount += weight;
      const agency = (v.agency || '').toLowerCase();
      if (agency.includes('home care providers') || agency.includes('happy at home')) {
        assistcareVisits += weight;
      } else if (agency.includes('pathfinder')) {
        pathfinderVisits += weight;
      }
    }
  });

  const pathfinderExtra = Math.max(0, pathfinderVisits - 25);
  const assistcareTotal = assistcareVisits + pathfinderExtra;
  const otherAgencies = finalCount - assistcareTotal;

  // --- Projections ---
  const lastSat = finalWeek.saturday;
  const nextSat = new Date(lastSat);
  nextSat.setDate(lastSat.getDate() + 7);
  const fmtWeek = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const lastSatStr = fmtWeek(lastSat);
  const nextSatStr = fmtWeek(nextSat);

  // Which Proj-1 weeks exist in the uploaded data?
  const projWeeksSet = new Set();
  (projections || []).forEach(proj => {
    if (proj.projection_type === 'Proj 1' && proj.week_ending) projWeeksSet.add(proj.week_ending);
  });

  // "Proj 1" = the UPCOMING week (the Saturday after the last completed week).
  // If that week hasn't been uploaded yet, fall back to the latest available
  // week that is still AFTER the last completed week — never a stale week.
  let projWeekStr = nextSatStr;
  if (!projWeeksSet.has(nextSatStr)) {
    const futureWeeks = [...projWeeksSet].filter(w => w > lastSatStr).sort();
    projWeekStr = futureWeeks.length ? futureWeeks[futureWeeks.length - 1] : nextSatStr;
  }
  // "Last Week Proj 1" = the Proj 1 for the week BEFORE the current projection week
  // (i.e., what was projected for the week the "Final" actuals now cover). Anchored to
  // projWeekStr − 7 days so it always tracks one week behind the current Proj 1. If that
  // exact week has no Proj 1 record (e.g., a mis-dated historical upload), fall back to the
  // nearest earlier uploaded Proj 1 week instead of showing 0.
  const projWeekDate = new Date(projWeekStr + 'T00:00:00');
  const prevWeekDate = new Date(projWeekDate);
  prevWeekDate.setDate(projWeekDate.getDate() - 7);
  let prevProjWeekStr = fmtWeek(prevWeekDate);
  if (!projWeeksSet.has(prevProjWeekStr)) {
    const earlierWeeks = [...projWeeksSet].filter(w => w < projWeekStr).sort();
    if (earlierWeeks.length) prevProjWeekStr = earlierWeeks[earlierWeeks.length - 1];
  }

  let proj1Count = 0;
  let lastWeekProj1 = 0;
  let totalEvals = 0;
  let totalDischarges = 0;

  const isEval = (name) => {
    const n = (name || '').toLowerCase();
    // Exclude re-evaluations first (all variations)
    if (n.includes('re-eval') || n.includes('re eval') || n.includes('reeval')) return false;
    // Then check for eval terms
    if (n.includes('eval') || n.includes('start of care') || n.includes('soc visit')) return true;
    return false;
  };
  const isDischarge = (name) => {
    const n = (name || '').toLowerCase();
    return n.includes('discharge') || n.includes('d/c') || n.includes('dc visit') || n.includes('recertification');
  };

  (projections || []).forEach(proj => {
    if (proj.projection_type !== 'Proj 1') return;

    (proj.visit_details || []).forEach(detail => {
      const w = getVisitWeight(detail.visit_name || detail.task_name, detail.status, trackingConfig);

      if (proj.week_ending === projWeekStr) {
        proj1Count += w;
        if (isEval(detail.visit_name || detail.task_name)) totalEvals += w;
        else if (isDischarge(detail.visit_name || detail.task_name)) totalDischarges += w;
      }
      if (proj.week_ending === prevProjWeekStr) {
        lastWeekProj1 += w;
      }
    });
  });

  const totalForPct = finalCount + totalMV;
  const mvPercentage = totalForPct > 0 ? Math.round((totalMV / totalForPct) * 1000) / 10 : 0;

  const finalLabel = `${String(lastSat.getMonth() + 1).padStart(2, '0')}/${String(lastSat.getDate()).padStart(2, '0')}`;

  return {
    week_ending: lastSatStr,
    weekEndingLabel: finalLabel,
    assistcare_happy_at_home: assistcareTotal,
    all_other_agencies: otherAgencies,
    final: finalCount,
    proj_1: proj1Count,
    last_week_proj_1: lastWeekProj1,
    total_evals: totalEvals,
    total_discharges: totalDischarges,
    difference: totalEvals - totalDischarges,
    mv_percentage: mvPercentage,
    total_notes: finalCount,
    total_mv: totalMV,
    mv_louisiana: mvLA,
    mv_texas: mvTX,
  };
}
